import { useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/TextLayer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import { apiFetchRaw } from '../../../lib/api-client'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url,
).toString()

// Live erhverv contract preview — same look/behaviour as the residential
// PdfPreviewPanel, but posts the erhverv wizard state to the erhverv endpoint.
interface Props {
  body: Record<string, unknown>
  fetchKey: number
  zoom?: number
  onPdfLoad?: (url: string) => void
  onNumPages?: (n: number) => void
}

export function ErhvervPreviewPanel({ body, fetchKey, zoom = 1.0, onPdfLoad, onNumPages }: Props) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [numPages, setNumPages] = useState(0)
  const [pageWidth, setPageWidth] = useState(600)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = bodyRef.current
    if (!el) return
    const obs = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width
      if (w) setPageWidth(Math.min(w - 80, 760))
    })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    async function run() {
      setLoading(true)
      setError(null)
      try {
        const res = await apiFetchRaw('/api/v1/contracts/erhverv-pdf-preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: controller.signal,
        })
        if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`)
        const blob = await res.blob()
        if (cancelled) return
        const newUrl = URL.createObjectURL(blob)
        setPdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return newUrl })
        onPdfLoad?.(newUrl)
      } catch (err) {
        if (!cancelled && (err as Error).name !== 'AbortError') {
          setError(err instanceof Error ? err.message : 'Fejl')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => { cancelled = true; controller.abort() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey])

  const scaledWidth = Math.round(pageWidth * zoom)

  return (
    <div ref={bodyRef} style={{ flex: 1, overflowY: 'auto', background: '#f0f0f0', padding: '28px 0 40px' }}>
      {loading && !pdfUrl && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: '#6b7280', fontSize: 13 }}>
          Genererer PDF…
        </div>
      )}
      {error && <div style={{ padding: 24, color: '#dc2626', fontSize: 12, textAlign: 'center' }}>{error}</div>}
      {pdfUrl && (
        <Document file={pdfUrl} onLoadSuccess={({ numPages: n }) => { setNumPages(n); onNumPages?.(n) }} loading={null}>
          {Array.from({ length: numPages }, (_, i) => (
            <div key={i} id={`pdf-page-${i + 1}`} style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <div style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)', lineHeight: 0 }}>
                <Page pageNumber={i + 1} width={scaledWidth} renderTextLayer={false} renderAnnotationLayer />
              </div>
            </div>
          ))}
        </Document>
      )}
      {loading && pdfUrl && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: 'rgba(0,0,0,0.7)', color: 'white', padding: '6px 14px', borderRadius: 20, fontSize: 12 }}>
          Opdaterer…
        </div>
      )}
    </div>
  )
}
